import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  ROZA_CLOUD_CYCLE_MS,
  ROZA_CLOUD_HOLD_MS,
  ROZA_CLOUD_OUT_MS,
  ROZA_CLOUD_STAGGER_MS,
} from './gameplayConstants';
import { RozaCloudModSvg } from './RozaCloudModSvg';
import './LevelRozaCloudOverlay.css';

type RozaCloudPhase = 'active' | 'out';

const PHASE_MS: Record<RozaCloudPhase, number> = {
  active: ROZA_CLOUD_HOLD_MS,
  out: ROZA_CLOUD_OUT_MS,
};

const NEXT_PHASE: Record<RozaCloudPhase, RozaCloudPhase | null> = {
  active: 'out',
  out: null,
};

type LevelRozaCloudOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

export function LevelRozaCloudOverlay({
  isPaused,
  onComplete,
}: LevelRozaCloudOverlayProps) {
  const [phase, setPhase] = useState<RozaCloudPhase>('active');
  const [shown, setShown] = useState(false);
  const remainingMsRef = useRef(PHASE_MS.active);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setShown(true);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    if (phase === 'out') {
      setShown(false);
    }
  }, [phase]);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();
    const tick = (now: number) => {
      remainingMsRef.current -= now - lastNow;
      lastNow = now;
      if (remainingMsRef.current > 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      const next = NEXT_PHASE[phase];
      if (next == null) {
        onCompleteRef.current();
        return;
      }
      remainingMsRef.current = PHASE_MS[next];
      setPhase(next);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [phase, isPaused]);

  const className = [
    'level-roza-cloud-overlay',
    shown ? 'level-roza-cloud-overlay--shown' : '',
    phase === 'active' ? 'level-roza-cloud-overlay--active' : '',
    isPaused ? 'level-roza-cloud-overlay--paused' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      style={
        {
          '--roza-cloud-cycle-ms': `${ROZA_CLOUD_CYCLE_MS}ms`,
          '--roza-cloud-out-ms': `${ROZA_CLOUD_OUT_MS}ms`,
        } as CSSProperties
      }
      aria-hidden
    >
      <RozaCloudModSvg staggerMs={ROZA_CLOUD_STAGGER_MS} />
    </div>
  );
}
